import React, { useState, useEffect } from 'react';
import { placementsAPI } from '../api';
import { toast } from 'react-toastify';
import './PlacementsManager.css'; // Import external CSS

const PlacementsManager = () => {
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    studentName: '',
    course: '',
    company: '',
    package: '',
    quote: '',
    year: new Date().getFullYear(),
    isActive: true
  });

  useEffect(() => {
    fetchPlacements();
  }, []);

  const fetchPlacements = async () => {
    try {
      const response = await placementsAPI.getAllAdmin();
      setPlacements(response.data);
    } catch (error) {
      toast.error('Failed to fetch placements');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (placement = null) => {
    if (placement) {
      setEditingPlacement(placement);
      setFormData({
        studentName: placement.studentName,
        course: placement.course,
        company: placement.company,
        package: placement.package,
        quote: placement.quote,
        year: placement.year,
        isActive: placement.isActive
      });
    } else {
      setEditingPlacement(null);
      setFormData({
        studentName: '',
        course: '',
        company: '',
        package: '',
        quote: '',
        year: new Date().getFullYear(),
        isActive: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingPlacement(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPlacement) {
        await placementsAPI.update(editingPlacement._id, formData);
        toast.success('Placement updated successfully');
      } else {
        await placementsAPI.create(formData);
        toast.success('Placement created successfully');
      }
      handleCloseModal();
      fetchPlacements();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this placement record?')) {
      try {
        await placementsAPI.delete(id);
        toast.success('Placement deleted successfully');
        fetchPlacements();
      } catch (error) {
        toast.error('Failed to delete placement');
      }
    }
  };

  // Filter placements based on search and status
  const filteredPlacements = placements.filter(placement => {
    const matchesSearch = placement.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         placement.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         placement.course.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && placement.isActive) ||
                         (filterStatus === 'inactive' && !placement.isActive);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading placements...</p>
      </div>
    );
  }

  return (
    <div className="placements-manager">
      {/* Animated Background */}
      <div className="bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="manager-container">
        {/* Header Section */}
        <div className="manager-header">
          <div className="header-content">
            <div className="header-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" />
                <path d="M2 17L12 22L22 17" />
                <path d="M2 12L12 17L22 12" />
                <path d="M12 12V22" />
              </svg>
            </div>
            <div>
              <h1>Placements Manager</h1>
              <p className="header-subtitle">Manage student placement records and testimonials</p>
            </div>
          </div>
          <button className="btn-add" onClick={() => handleOpenModal()}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add New Placement
          </button>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="search-box">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by student, company, or course..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All ({placements.length})
            </button>
            <button 
              className={`filter-tab ${filterStatus === 'active' ? 'active' : ''}`}
              onClick={() => setFilterStatus('active')}
            >
              Active ({placements.filter(p => p.isActive).length})
            </button>
            <button 
              className={`filter-tab ${filterStatus === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilterStatus('inactive')}
            >
              Inactive ({placements.filter(p => !p.isActive).length})
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">{placements.length}</span>
              <span className="stat-label">Total Placements</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">{placements.filter(p => p.isActive).length}</span>
              <span className="stat-label">Active</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">{placements.reduce((sum, p) => sum + parseInt(p.package || 0), 0) / (placements.length || 1)} LPA</span>
              <span className="stat-label">Avg Package</span>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-wrapper">
          <table className="placements-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Course</th>
                <th>Company</th>
                <th>Package</th>
                <th>Year</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlacements.map((placement, index) => (
                <tr key={placement._id} className="fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                  <td>
                    <div className="student-info">
                      <div className="student-avatar">
                        {placement.studentName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="student-name">{placement.studentName}</div>
                        <div className="student-quote-preview">{placement.quote.substring(0, 50)}...</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="course-badge">{placement.course}</span>
                  </td>
                  <td className="company-cell">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                    </svg>
                    {placement.company}
                  </td>
                  <td className="package-cell">
                    <span className="package-value">{placement.package}</span>
                  </td>
                  <td>{placement.year}</td>
                  <td>
                    <span className={`status-badge ${placement.isActive ? 'status-active' : 'status-inactive'}`}>
                      <span className="status-dot"></span>
                      {placement.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn edit-btn" 
                        onClick={() => handleOpenModal(placement)}
                        title="Edit"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                          <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                        </svg>
                      </button>
                      <button 
                        className="action-btn delete-btn" 
                        onClick={() => handleDelete(placement._id)}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPlacements.length === 0 && (
                <tr>
                  <td colSpan="7" className="empty-state">
                    <div className="empty-content">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <p>No placements found</p>
                      <button className="btn-add-small" onClick={() => handleOpenModal()}>
                        Add your first placement
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" />
                  <path d="M2 17L12 22L22 17" />
                  <path d="M2 12L12 17L22 12" />
                </svg>
                <h2>{editingPlacement ? 'Edit Placement' : 'Add New Placement'}</h2>
              </div>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Student Name *</label>
                <input 
                  type="text" 
                  name="studentName" 
                  value={formData.studentName} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="Enter student's full name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Course *</label>
                <input 
                  type="text" 
                  name="course" 
                  value={formData.course} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="e.g., B.Tech Computer Science"
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company *</label>
                  <input 
                    type="text" 
                    name="company" 
                    value={formData.company} 
                    onChange={handleInputChange} 
                    required 
                    placeholder="Company name"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Package *</label>
                  <input 
                    type="text" 
                    name="package" 
                    value={formData.package} 
                    onChange={handleInputChange} 
                    placeholder="e.g., ₹12 LPA"
                    required 
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year of Placement</label>
                  <input 
                    type="number" 
                    name="year" 
                    value={formData.year} 
                    onChange={handleInputChange} 
                    className="form-input"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="isActive" 
                      checked={formData.isActive} 
                      onChange={handleInputChange} 
                    />
                    <span>Active Placement</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Testimonial Quote *</label>
                <textarea 
                  name="quote" 
                  value={formData.quote} 
                  onChange={handleInputChange} 
                  rows="4" 
                  required 
                  placeholder="Write a testimonial from the student about their placement experience..."
                  className="form-textarea"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingPlacement ? 'Update Placement' : 'Save Placement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementsManager;